import { useEffect, useRef, useCallback, useState, useMemo } from 'react';
import {
  microAppInit,
  microAppUnmount,
  microAppOpen,
  sendMessageToMicroApp,
  MicroAppActionType,
  MicroAppRequest,
  TemplateInfo,
  MainInitData,
  VoiceInfo,
  DigitalHumanInfo,
  VIDEO_STYLE_TYPE
} from '@bhb-frontend/style-editor-microapp';
import { AccountData } from '@/services/account';
import { agentCompanyAssetHooks, agentUserTemplateListHooks } from '@/manager';
import i18n from "@/locales";

interface ActorInfo extends DigitalHumanInfo {
  modelId: string;
}

interface OnSaveSuccessParams {
  /** 编辑器返回：风格保存参数 */
  styleSaveParams: MicroAppRequest['payload']['styleSaveParams'];
  /** 数字人模型 ID */
  actorModelId: string;
}

interface UseStyleEditorProps {
  templateList?: TemplateInfo[];
  actors?: ActorInfo[];
  voices?: Omit<VoiceInfo, 'coverUrl'>[];
  account?: AccountData;
  /** 启用预设模板列表 */
  openPresetTemplates?: boolean;
  /** 启用公共数字人 */
  openPublicActors?: boolean;
  /** 启用公共音频 */
  openPublicVoices?: boolean;
  // onSaveSuccess: (data: MicroAppRequest) => Promise<void>;
  onSaveSuccess: (params: OnSaveSuccessParams) => Promise<void>;
  onClose?: () => Promise<void>;
  onMounted?: () => void;
}

const env = import.meta.env.MODE === "production"
  ? "prod"
  : import.meta.env.MODE === "gray" ? "gray" : "test";

// 预设模板分类列表
const getPresetTemplateCategories = () => [
  { id: '', name: i18n.t('style_editor_microapp.preset_template.all') },
]

// 公共数字人分类列表
const getPublicActorCategoryList = () => [
  { id: 'male', name: i18n.t('style_editor_microapp.public_actor.male') },
  { id: 'female', name: i18n.t('style_editor_microapp.public_actor.female') },
]

// 公共音频分类列表
const getPublicVoiceCategoryList = () => [
  { id: 'minimax', name: i18n.t('style_editor_microapp.public_voice.minimax') },
  { id: 'cartesia', name: i18n.t('style_editor_microapp.public_voice.cartesia') },
  { id: 'elevenlabs', name: i18n.t('style_editor_microapp.public_voice.elevenlabs') },
]

/**
 * 风格编辑器微应用 hook
 * @returns 
 */
export const useStyleEditorMicroApp = ({
  actors,
  voices,
  account,
  // templateList,
  openPresetTemplates = false,
  openPublicActors,
  openPublicVoices,
  onSaveSuccess,
  onClose,
}: UseStyleEditorProps) => {
  const originImageRef = useRef(null);
  const imageObserverRef = useRef<MutationObserver | null>(null);
  // 初始化中
  const [initialAppLoading, setInitialAppLoading] = useState<boolean>(false);

  // 预设模板列表
  const { templates: presetTemplates, loading: presetLoading } = agentCompanyAssetHooks.useTemplatesData(true, openPresetTemplates);

  // 公共数字人/配音列表
  const {
    publicActors,
    publicVoices,
    loading: publicLoading,
    fetchPublicActors,
    fetchPublicAudios,
  } = agentUserTemplateListHooks.usePublicTemplatesData();

  useEffect(() => {
    // 同步 loading 状态
    setInitialAppLoading(publicLoading || presetLoading);
  }, [publicLoading, presetLoading]);

  // ================== 预设模板相关 ==================
  // 过滤出可用的预设模板
  const enablePresetTemplates = presetTemplates ? presetTemplates?.filter(item => item.enabled && item.status === 1) : [];

  // 构建编辑器需要的模板列表字段
  const presetTemplateList = useMemo(() => {
    if (!presetLoading) {
      return enablePresetTemplates?.map(item => ({
        id: item.templateId,
        name: item.templateName,
        coverUrl: item.coverUrl,
        // videoUrl: item.coverVideo,
        videoUrl: '',
        videoStyleType: item.scene as VIDEO_STYLE_TYPE,
      }))
    }
  }, [presetTemplates])

  // ==================== 数字人相关 ====================
  // 消息处理：发送数字人列表
  const handleSendActor = useCallback((data: MicroAppRequest) => {
    const payload = data?.payload;

    let sendActors = [...actors];

    if (payload.searchKey === 'name' && payload.searchValue) {
      sendActors = sendActors.filter(item => item.name.includes(payload.searchValue))
    }

    sendMessageToMicroApp('style-editor-microapp', {
      type: 'RESPONSE',
      requestId: data.requestId,
      data: {
        total: actors.length,
        results: sendActors,
      },
      success: true,
    });
    console.log('[miuta]发送数字人列表', { sendActors })
  }, [actors]);

  // 消息处理，发送公共数字人列表
  const handleSendPublicActor = useCallback(async (data: MicroAppRequest) => {
    // 没开启公共数字人功能，不处理
    if (!openPublicActors) return;

    const payload = data.payload;
    const searchKey = payload.searchKey;
    const searchValue = payload.searchValue;

    // 请求公共数字人数据
    const publicActorsData = await fetchPublicActors({
      sid: payload.sid ? Number(payload.sid) : null,
      gender: searchKey === 'categoryId' ? searchValue : undefined, // 目前只按性别筛选
      name: searchKey === 'name' ? searchValue : undefined,
    })

    // 发送给编辑器的列表数据
    const sendPublicActors = publicActorsData?.actors.map(item => ({
      id: item.id,
      name: item.name,
      coverUrl: item.imageUrl,
      renderUrl: item.imageUrl,
      width: item.width,
      height: item.height,
      // 是否绿幕数字人
      isGreen: item.situation ? item.situation.split(',').includes('Green-Screen') : false,
      // 是否公共数字人
      isPublic: true,
    }))

    sendMessageToMicroApp('style-editor-microapp', {
      type: 'RESPONSE',
      requestId: data.requestId,
      data: {
        total: publicActorsData.total,
        sid: publicActorsData.sid,
        results: sendPublicActors
      },
      success: true,
    });
    console.log('[miuta]发送公共数字人列表', { payload, sendPublicActors })
  }, [publicActors]);

  // 消息处理，发送公共数字人分类列表
  const handleSendPublicActorCategory = useCallback((data: MicroAppRequest) => {
    const publicActorCategoryList = getPublicActorCategoryList();
    sendMessageToMicroApp('style-editor-microapp', {
      type: 'RESPONSE',
      requestId: data.requestId,
      data: {
        total: publicActorCategoryList.length,
        results: openPublicActors ? publicActorCategoryList : []
      },
      success: true,
    });
    console.log('[miuta]发送公共数字人分类列表', { publicActorCategoryList })
  }, [publicActors, openPublicActors]);

  // ==================== 配音相关 ====================
  // 消息处理：发送配音列表
  const handleSendVoice = useCallback((data: MicroAppRequest) => {
    const payload = data?.payload;

    let sendVoices = voices.map(item => ({
      ...item,
      coverUrl: `${window.location.origin}/logo.png`,
    }))

    if (payload.searchKey === 'name' && payload.searchValue) {
      sendVoices = sendVoices.filter(item => item.name.includes(payload.searchValue))
    }

    sendMessageToMicroApp('style-editor-microapp', {
      type: 'RESPONSE',
      requestId: data.requestId,
      data: {
        total: voices.length,
        results: sendVoices
      },
      success: true,
    });
    console.log('[miuta]发送配音列表', { sendVoices })
  }, [voices]);

  // 消息处理，发送公共配音列表
  const handleSendPublicVoice = useCallback(async (data: MicroAppRequest) => {
    // 没开启公共配音功能，不处理
    if (!openPublicVoices) return;

    const payload = data?.payload;
    const searchValue = payload?.searchValue;
    const searchKey = payload?.searchKey;

    // 请求公共配音数据
    const publicVoicesData = await fetchPublicAudios({
      sid: payload.sid ? Number(payload.sid) : null,
      provider: searchKey === 'categoryId' ? searchValue : undefined, // 目前只按供应商筛选
      name: searchKey === 'name' ? searchValue : undefined,
    })

    // 发送的列表数据
    const sendPublicVoice = publicVoicesData?.templates.map(item => ({
      id: item.voiceTtsId,
      name: item.name,
      coverUrl: `${window.location.origin}/logo.png`,
      exampleUrl: item.sampleUrl,
      isPublic: true,
    }))

    sendMessageToMicroApp('style-editor-microapp', {
      type: 'RESPONSE',
      requestId: data.requestId,
      data: {
        total: publicVoicesData.pagination.total,
        sid: publicVoicesData.pagination.sid,
        results: sendPublicVoice
      },
      success: true,
    });
    console.log('[miuta]发送公共配音列表', { payload, sendPublicVoice })
  }, [publicVoices]);

  // 消息处理，发送公共配音分类列表
  const handleSendPublicVoiceCategory = useCallback((data: MicroAppRequest) => {
    const publicVoiceCategoryList = getPublicVoiceCategoryList();
    sendMessageToMicroApp('style-editor-microapp', {
      type: 'RESPONSE',
      requestId: data.requestId,
      data: {
        total: publicVoiceCategoryList.length,
        results: openPublicVoices ? publicVoiceCategoryList : []
      },
      success: true,
    });
    console.log('[miuta]发送公共配音分类列表', { publicVoiceCategoryList })
  }, [publicVoices, openPublicVoices]);

  // 核心消息监听分发
  const handleMicroAppMessage = useCallback((data: MicroAppRequest) => {
    switch (data.type) {
      // 获取数字人列表
      case MicroAppActionType.GET_DIGITAL_HUMAN_LIST:
        handleSendActor(data);
        break;
      // 获取公共数字人列表
      case MicroAppActionType.GET_PUBLIC_DIGITAL_HUMAN_LIST:
        handleSendPublicActor(data);
        break;
      // 获取公共数字人分类列表
      case MicroAppActionType.GET_PUBLIC_DIGITAL_HUMAN_CATEGORY:
        handleSendPublicActorCategory(data);
        break;
      // 获取配音列表
      case MicroAppActionType.GET_VOICE_LIST:
        handleSendVoice(data);
        break;
      // 获取公共配音列表
      case MicroAppActionType.GET_PUBLIC_VOICE_LIST:
        handleSendPublicVoice(data);
        break;
      // 获取公共配音分类列表
      case MicroAppActionType.GET_PUBLIC_VOICE_CATEGORY:
        handleSendPublicVoiceCategory(data);
        break;
      case MicroAppActionType.SAVE_STYLE: {
        const styleSaveParams = data.payload.styleSaveParams;

        // 选择了公共数字人，则从公共数字人列表中获取模型ID
        const _actor = !styleSaveParams.isPublicDigitalHuman ? actors : publicActors;

        const actorModelId = _actor.find(item => item.id === styleSaveParams.digitalHumanId)?.modelId || '';

        console.log('[miuta]保存风格', {
          data,
          _actor,
          actorModelId,
          digitalHumanId: styleSaveParams.digitalHumanId,
          isPublicDigitalHuman: styleSaveParams.isPublicDigitalHuman,
          publicActors,
        });
        onSaveSuccess({ styleSaveParams, actorModelId });
      }
        break;
      case MicroAppActionType.CLOSE:
        console.log('[miuta]关闭弹窗', { data })
        onClose?.();
        break;
    }
  }, [handleSendActor, handleSendVoice, onSaveSuccess, onClose, actors, publicActors]);

  // 图片跨域 hack 处理
  const observeImages = (root: HTMLElement, onImage: (img: HTMLImageElement) => void) => {
    root.querySelectorAll('img').forEach(img => onImage(img));
    const observer = new MutationObserver(() => {
      root.querySelectorAll('img').forEach(img => onImage(img));
    });
    observer.observe(root, { childList: true, subtree: true });
    return observer;
  };

  // 打开编辑器方法
  const openEditor = useCallback(({
    editIdParam,
    templateId
  }: { editIdParam?: MainInitData['editIdParam'], templateId?: string }) => {
    microAppOpen('style-editor-microapp', {
      appId: 'Miuta',
      visible: true,
      userId: account?.id || '',
      useDefaultData: false,
      templateSearchSwitch: 'disable',
      templateList: presetTemplateList,
      presetTemplateCategories: getPresetTemplateCategories(),
      editIdParam: editIdParam,
      customizeTemplateId: templateId,
      creatorInfo: {
        id: account?.id || '',
        name: account?.username || '',
        avatar: account?.avatar || '',
      }
    });
    console.log('[miuta]发送编辑参数', { editIdParam, templateId, presetTemplateList });
  }, [presetTemplateList, actors, account]);

  // 微应用挂载
  const handleMounted = useCallback(() => {
    const microApp = document.querySelector('[name="style-editor-microapp"]') as HTMLElement;
    if (!microApp) return;

    const microAppWindow = microApp.children[0]?.ownerDocument?.defaultView;
    if (!microAppWindow) return;

    if (!originImageRef.current) originImageRef.current = microAppWindow.Image;

    // Proxy 构造函数以支持跨域
    microAppWindow.Image = new Proxy(originImageRef.current, {
      construct(target, args) {
        const img = new target(...args);
        img.crossOrigin = 'anonymous';
        return img;
      },
    });

    imageObserverRef.current = observeImages(microApp, (img) => {
      if (!img.crossOrigin) img.crossOrigin = 'anonymous';
    });
  }, []);

  // 微应用卸载
  const handleUnmounted = useCallback(() => {
    if (imageObserverRef.current) {
      imageObserverRef.current.disconnect();
      imageObserverRef.current = null;
    }
    const microApp = document.querySelector('[name="style-editor-microapp"]') as HTMLElement;
    const microAppWindow = microApp?.children[0]?.ownerDocument?.defaultView;
    if (microAppWindow && originImageRef.current) {
      microAppWindow.Image = originImageRef.current;
      originImageRef.current = null;
    }
    microAppUnmount('style-editor-microapp');
  }, []);

  useEffect(() => {
    microAppInit();
    return () => microAppUnmount('style-editor-microapp');
  }, []);

  return {
    env,
    initialAppLoading,
    enablePresetTemplates,
    handleMicroAppMessage,
    handleMounted,
    handleUnmounted,
    openEditor
  };
};